import {analyzeImageSafeAreas, type EdgeSafeAreas, type SafeArea} from './ImageSafeAreaAnalyzer';

/**
 * Analyze an image element to find safe areas for text placement
 * Uses AOI (Area of Interest) data from image data attributes if available
 * No image loading required - works purely from AOI percentages
 */
export function getImageSafeAreas(imgEl: HTMLImageElement): EdgeSafeAreas {
	return analyzeImageSafeAreas(imgEl);
}

/**
 * Find the best safe area for placing text overlay
 * Selects corner based on combined area of adjacent edges
 */
export function getBestTextPlacement(areas: EdgeSafeAreas): SafeArea | null {
	// Calculate combined edge areas for each corner
	const cornerScores = [
		{
			corner: 'top-left' as const,
			area: areas.topLeft,
			score: (areas.top?.areaPercent || 0) + (areas.left?.areaPercent || 0)
		},
		{
			corner: 'top-right' as const,
			area: areas.topRight,
			score: (areas.top?.areaPercent || 0) + (areas.right?.areaPercent || 0)
		},
		{
			corner: 'bottom-left' as const,
			area: areas.bottomLeft,
			score: (areas.bottom?.areaPercent || 0) + (areas.left?.areaPercent || 0)
		},
		{
			corner: 'bottom-right' as const,
			area: areas.bottomRight,
			score: (areas.bottom?.areaPercent || 0) + (areas.right?.areaPercent || 0)
		}
	];

	// Sort by combined edge area score, prefer larger combined areas
	// If scores are equal, prefer bottom corners (bottom-left > bottom-right > top-left > top-right)
	cornerScores.sort((a, b) => {
		if (b.score === a.score) {
			// Tiebreaker: prefer bottom over top, then left over right
			const priority = {'bottom-left': 0, 'bottom-right': 1, 'top-left': 2, 'top-right': 3};
			return priority[a.corner] - priority[b.corner];
		}
		return b.score - a.score;
	});

	// Return the corner area with the highest combined edge score
	for (const {area} of cornerScores) {
		if (area) return area;
	}

	return null;
}

/**
 * Apply safe area as CSS custom properties to an element
 */
export function applySafeAreaStyles(element: HTMLElement, area: SafeArea) {
	element.style.setProperty('--safe-area-x', `${area.xPercent}%`);
	element.style.setProperty('--safe-area-y', `${area.yPercent}%`);
	element.style.setProperty('--safe-area-width', `${area.widthPercent}%`);
	element.style.setProperty('--safe-area-height', `${area.heightPercent}%`);
	element.dataset.safeAreaCorner = area.corner;
	element.classList.add(`card--safe-area-${area.corner}`);
}
